import type { TFunction } from "i18next";

import { EventOrigin, Events, track } from "apis/procore";
import type { AppDispatch, AppStore } from "app/store";
import type { DispatchHidden } from "contexts/hidden/reducer";
import type { DispatchHighlighted } from "contexts/highlighted/reducer";
import type { DispatchSelectionBasket } from "contexts/selectionBasket/reducer";
import { capturePropertyFilter } from "features/properties/slice";
import { applyIsolationSelectionVisibility, selectionIdIsHidden } from "features/render/sheetIsolationVisibility";
import { ObjectVisibility } from "features/render/types";
import {
    captureVisibilitySnapshot,
    hideObjects,
    isolateObjects,
    isolateObjectsInXRayMode,
    showObjects,
    unisolateObjects,
    unisolateObjectsFromXRayMode,
} from "utils/objectActions";
import type { IdList, SelectionSet } from "utils/selectionSet";

export interface BulkAction {
    id: string;
    label: string;
    onClick: () => void;
    disabled: boolean;
    dividerAbove?: boolean;
}

interface CreateBulkActionsDependencies {
    t: TFunction;
    dispatch: AppDispatch;
    /** Read at click time for the state these actions' undo restores. */
    store: AppStore;
    dispatchHighlighted: DispatchHighlighted;
    dispatchHidden: DispatchHidden;
    dispatchSelectionBasket: DispatchSelectionBasket;
    defaultVisibility: ObjectVisibility;
    /** The visibility that was active before the user isolated. Set by isolateObjects/isolateObjectsInXRayMode.
     * When undefined, the current defaultVisibility was not set by the Isolate action. */
    preIsolationVisibility: ObjectVisibility | undefined;
    flyToSelected: () => void;
    currentMainObject?: number;
    onActionComplete?: () => void;
    hiddenIds: SelectionSet;
    lastHiddenIds: Iterable<number>;
    isolationVisibility?: {
        visibleIds: SelectionSet | undefined;
        keepIds: SelectionSet | undefined;
        setIdsVisible: (ids: IdList, visible: boolean) => void;
    };
    /**
     * Full geometry id set (main objects + their descendants) used for isolation.
     * Defaults to `highlightedIds` when omitted (backward-compat for non-V2 callers).
     * Isolate must keep all geometry fragments visible, not just resolved first-object ids.
     */
    geometryIds?: IdList;
    /** Known sheet ids. During isolation those ids are hidden through the hidden set, not the keep-set. */
    pdfObjectIds?: { has(id: number): boolean; size: number };
    /** The origin to report in analytics events. Defaults to ObjectTree. */
    origin?: EventOrigin;
}

export function createBulkActions(
    selectedCount: number,
    highlightedIds: IdList,
    dependencies: CreateBulkActionsDependencies,
): BulkAction[] {
    const {
        t,
        dispatch,
        store,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        defaultVisibility,
        preIsolationVisibility,
        flyToSelected,
        currentMainObject,
        onActionComplete,
        hiddenIds,
        lastHiddenIds,
        isolationVisibility,
        geometryIds,
        pdfObjectIds,
        origin = EventOrigin.ObjectTree,
    } = dependencies;
    const isolationVisibleIds = isolationVisibility?.visibleIds;
    const isolationIds = geometryIds ?? highlightedIds;

    const areAllHidden =
        isolationIds.length > 0 &&
        Array.from(isolationIds).every((id) => selectionIdIsHidden(id, hiddenIds, isolationVisibleIds, pdfObjectIds));
    // Only show "Unisolate" when we have a stored restore visibility.
    // preIsolationVisibility being set is the signal that the current Transparent/Xray mode has an associated
    // prior visibility to restore (e.g., from isolateObjects/isolateObjectsInXRayMode or bookmark/viewpoint restore),
    // not another feature (BimTrack, BimCollab, Deviations, etc.) changing it without storing a restore state.
    const areAllIsolated =
        highlightedIds.length > 0 &&
        !areAllHidden &&
        defaultVisibility === ObjectVisibility.Transparent &&
        preIsolationVisibility !== undefined;
    const areAllIsolatedFromXRayMode =
        highlightedIds.length > 0 &&
        !areAllHidden &&
        defaultVisibility === ObjectVisibility.Xray &&
        preIsolationVisibility !== undefined;

    return [
        {
            id: "hide-selected",
            label: areAllHidden ? t("unhideSelected") : t("hideSelected"),
            onClick: () => {
                if (isolationVisibleIds && isolationVisibility) {
                    applyIsolationSelectionVisibility(
                        isolationIds,
                        pdfObjectIds,
                        areAllHidden,
                        (modelIds, visible) => isolationVisibility.setIdsVisible(modelIds, visible),
                        (sheetIds) => {
                            hideObjects(sheetIds, {
                                dispatch,
                                dispatchHighlighted,
                                dispatchHidden,
                                dispatchSelectionBasket,
                                currentMainObject,
                                currentHiddenIds: hiddenIds,
                                currentLastHiddenIds: lastHiddenIds,
                                preserveSelection: true,
                            });
                        },
                        (sheetIds) => {
                            showObjects(sheetIds, {
                                dispatch,
                                dispatchHidden,
                            });
                        },
                    );
                } else if (areAllHidden) {
                    showObjects(isolationIds, {
                        dispatch,
                        dispatchHidden,
                    });
                } else {
                    hideObjects(isolationIds, {
                        dispatch,
                        dispatchHighlighted,
                        dispatchHidden,
                        dispatchSelectionBasket,
                        currentMainObject,
                        currentHiddenIds: hiddenIds,
                        currentLastHiddenIds: lastHiddenIds,
                        preserveSelection: true,
                        includeMainObjectToHide: true,
                    });
                }
                void track(Events.bimObjects.hidden, { origin });
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
        {
            id: "isolate",
            label: areAllIsolated ? t("unisolate") : t("isolate"),
            onClick: () => {
                if (areAllIsolated) {
                    unisolateObjects({
                        dispatch,
                        isolationIds: isolationVisibility?.keepIds?.sortedIds ?? [],
                        restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                    });
                    void track(Events.bimObjects.unisolated, { origin });
                } else {
                    isolateObjects({
                        dispatch,
                        isolationIds,
                        currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                        visibilityBefore: captureVisibilitySnapshot(store.getState()),
                        propertyFilter: capturePropertyFilter(store.getState()),
                    });
                    void track(Events.bimObjects.isolated, { origin });
                }
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
        {
            id: "isolate-xray",
            label: areAllIsolatedFromXRayMode ? t("unisolateFromXRayMode") : t("isolateInXRayMode"),
            onClick: () => {
                if (areAllIsolatedFromXRayMode) {
                    unisolateObjectsFromXRayMode({
                        dispatch,
                        restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                        propertyFilter: capturePropertyFilter(store.getState()),
                    });
                    void track(Events.bimObjects.unisolatedFromXray, { origin });
                } else {
                    isolateObjectsInXRayMode({
                        dispatch,
                        currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                        propertyFilter: capturePropertyFilter(store.getState()),
                    });
                    void track(Events.bimObjects.isolatedInXray, { origin });
                }
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
        {
            id: "zoom-to-selection",
            label: t("zoomToSelection"),
            onClick: () => {
                flyToSelected();
                void track(Events.bimObjects.zoomedToSelection, { origin });
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
    ];
}
